import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@context/ThemeContext';
import { ThemeName } from '@config/theme';

const THEME_OPTIONS: Record<ThemeName, { label: string; icon: string; description: string }> = {
  dark:      { label: 'Dark',      icon: '🌙', description: 'Dark slate'       },
  light:     { label: 'Light',     icon: '☀️',  description: 'Clean light'      },
  ocean:     { label: 'Ocean',     icon: '🌊', description: 'Deep blue & cyan' },
  sunset:    { label: 'Sunset',    icon: '🌅', description: 'Orange & red'     },
  forest:    { label: 'Forest',    icon: '🌲', description: 'Emerald green'    },
  cyberpunk: { label: 'Cyberpunk', icon: '⚡', description: 'Purple & pink'    },
};

export const ThemeSelector: React.FC = () => {
  const { currentTheme, setTheme, availableThemes, themeConfig } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const current = THEME_OPTIONS[currentTheme];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={menuRef} className="relative">
      {/* Trigger button — inherits header icon colors from theme */}
      <button
        onClick={() => setIsOpen(v => !v)}
        onMouseEnter={() => setIsOpen(true)}
        className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg transition-colors
                    ${themeConfig.header.iconColor} ${themeConfig.header.iconHover}`}
        title={`Theme: ${current.label}`}
        aria-label="Theme selector"
        aria-expanded={isOpen}
      >
        <span className="text-base leading-none">{current.icon}</span>
        <span className={`hidden sm:inline text-xs font-medium ${themeConfig.header.iconColor}`}>
          {current.label}
        </span>
        <svg className="w-3 h-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown — fully theme-aware */}
      {isOpen && (
        <div
          onMouseLeave={() => setIsOpen(false)}
          className={`absolute right-0 mt-2 w-52 rounded-xl border z-50 overflow-hidden
                      ${themeConfig.dropdown.background}
                      ${themeConfig.dropdown.border}
                      ${themeConfig.dropdown.shadow}`}
        >
          {/* Header row */}
          <div className={`px-4 py-2.5 border-b ${themeConfig.dropdown.headerBg} ${themeConfig.dropdown.divider}`}>
            <p className={`text-[10px] font-bold tracking-widest uppercase ${themeConfig.dropdown.textSecondary}`}>
              Choose Theme
            </p>
          </div>

          {/* Options */}
          <div className="p-1.5 space-y-0.5">
            {availableThemes.map(theme => {
              const info = THEME_OPTIONS[theme];
              const isActive = theme === currentTheme;
              return (
                <button
                  key={theme}
                  onClick={() => { setTheme(theme); setIsOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm
                              transition-all duration-150
                              ${isActive
                                ? `${themeConfig.dropdown.activeBg} ${themeConfig.dropdown.activeText} font-semibold`
                                : `${themeConfig.dropdown.text} ${themeConfig.dropdown.hoverBg} ${themeConfig.dropdown.hoverText}`
                              }`}
                >
                  <span className="text-lg leading-none">{info.icon}</span>
                  <div className="flex-1 text-left">
                    <p className="font-medium leading-tight">{info.label}</p>
                    <p className={`text-[11px] mt-0.5 ${themeConfig.dropdown.textSecondary}`}>{info.description}</p>
                  </div>
                  {isActive && (
                    <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className={`px-4 py-2 border-t ${themeConfig.dropdown.divider}`}>
            <p className={`text-[10px] ${themeConfig.dropdown.textSecondary}`}>
              Saved automatically
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
